<template>
  <div
    class="basic_privacy_cont_ w-full flex flex-col justify-center items-center"
  >
    <NavbarNew />
    <div
      class="basic_child_privacy_cont_ max-w-7xl w-full flex flex-col gap-4 justify-start md:px-6 px-4 py-6"
    >
      <h1>Privacy Policy</h1>
      <p class="text-sm mb-0">Effective Date: 10/07/2025</p>
      <p class="text-sm mb-0">Last updated: 10/07/2025</p>
      <p>
        Welcome to Decast. We respect your privacy and are committed to
        protecting your personal data. This Privacy Policy explains how we
        collect, use, store, share, and protect your data when you use Decast.
      </p>
      <p>
        This policy is built in accordance with the
        <a href="https://principles.fairdatasociety.org/" target="_blank"
          >Fair Data Society Principles</a
        >
        , GDPR, and other applicable data protection ethos and regulatory laws.
        .
      </p>
      <h2 class="text-2xl font-bold mt-4">Who Are We and What We Do:</h2>

      <p>
        Decast is a decentralized broadcasting and collaboration platform
        enabling users to cast, collaborate, and connect across distributed
        systems.
      </p>
      <p class="text-lg font-bold">
        For questions about this policy, contact us:
      </p>
      <ul>
        <li>
          <p>
            <strong>Email: </strong>
            <a href="mailto:support@decast.live" target="_blank"
              >support@decast.live</a
            >
          </p>
        </li>
        <li>
          <p>
            <strong>Address: </strong>

            463 Avenue Lalonde, Québec City, Sainte-Bernadette
          </p>
        </li>
        <li>
          <p>
            <strong>Data Protection Officer: </strong>
            Rupesh Choudhary
          </p>
        </li>
      </ul>

      <h2 class="text-2xl font-bold mt-4">Fair Data Principles and Ethos</h2>
      <p>
        Decast is committed to upholding the Fair Data Society principles,
        ensuring that your data rights are protected and respected. Our platform
        operates according to the following principles:
      </p>

      <div
        class="fair-data-principles grid grid-cols-1 md:grid-cols-2 gap-8 mb-4"
      >
        <div
          v-for="principle in fairDataPrinciples"
          :key="principle.number"
          class="principle-card border border-gray-600 border-opacity-50 hover:border-opacity-100 transition-all"
        >
          <div class="flex items-start gap-4 p-6 pl-0">
            <span class="principle-number text-primary font-bold text-xl">{{
              principle.number
            }}</span>
            <div class="flex flex-col gap-2">
              <h4 class="text-white font-bold text-xl">
                {{ principle.title }}
              </h4>
              <p class="text-sm text-gray-400 font-light">
                {{ principle.description }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <h3 class="text-xl font-bold mt-4 mb-2">Our Privacy Principles</h3>
      <p>Decast is committed to:</p>

      <ol>
        <li>
          <p><strong>Data Sovereignty:</strong> You own your data.</p>
        </li>
        <li>
          <p>
            <strong>Minimal Data Collection:</strong> We only collect what’s
            necessary.
          </p>
        </li>
        <li>
          <p>
            <strong>Transparency:</strong> You know exactly what data is
            collected and why.
          </p>
        </li>
        <li>
          <p>
            <strong>Control:</strong> You control your data, its access,
            sharing, and erasure.
          </p>
        </li>
      </ol>

      <h3 class="text-xl font-bold mt-4 mb-2">What Data We Collect</h3>

      <p>
        At Decast, we believe in minimal, purpose-driven data collection. <br />
        We collect only the information necessary to provide and improve our
        services in line with the
        <a href="https://principles.fairdatasociety.org/" target="_blank"
          >Fair Data Society Principles</a
        >, and we never sell your data.
      </p>
      <p>Here's what we collect - and why:</p>

      <h4 class="text-lg font-bold mt-4 mb-2">
        Account & Identity Information
      </h4>
      <div class="overflow-x-auto mb-4">
        <table
          class="w-full border-collapse border border-gray-600 border-opacity-50"
        >
          <thead>
            <tr class="bg-gray-800 bg-opacity-50">
              <th
                class="border border-gray-600 border-opacity-50 p-4 text-left text-white font-bold"
              >
                Data Type
              </th>
              <th
                class="border border-gray-600 border-opacity-50 p-4 text-left text-white font-bold"
              >
                Examples
              </th>
              <th
                class="border border-gray-600 border-opacity-50 p-4 text-left text-white font-bold"
              >
                Purpose
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in accountIdentityData"
              :key="item.dataType"
              class="hover:bg-gray-800 hover:bg-opacity-25 transition-all"
            >
              <td
                class="border border-gray-600 border-opacity-50 p-4 text-white font-medium"
              >
                {{ item.dataType }}
              </td>
              <td
                class="border border-gray-600 border-opacity-50 p-4 text-gray-300"
              >
                {{ item.examples }}
              </td>
              <td
                class="border border-gray-600 border-opacity-50 p-4 text-gray-300"
              >
                {{ item.purpose }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <h4 class="text-lg font-bold mt-4 mb-2">Contacts & Invitations</h4>
      <div class="overflow-x-auto mb-4">
        <table
          class="w-full border-collapse border border-gray-600 border-opacity-50"
        >
          <thead>
            <tr class="bg-gray-800 bg-opacity-50">
              <th
                class="border border-gray-600 border-opacity-50 p-4 text-left text-white font-bold"
              >
                Data Type
              </th>
              <th
                class="border border-gray-600 border-opacity-50 p-4 text-left text-white font-bold"
              >
                Examples
              </th>
              <th
                class="border border-gray-600 border-opacity-50 p-4 text-left text-white font-bold"
              >
                Purpose
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in contactsInvitationsData"
              :key="item.dataType"
              class="hover:bg-gray-800 hover:bg-opacity-25 transition-all"
            >
              <td
                class="border border-gray-600 border-opacity-50 p-4 text-white font-medium"
              >
                {{ item.dataType }}
              </td>
              <td
                class="border border-gray-600 border-opacity-50 p-4 text-gray-300"
              >
                {{ item.examples }}
              </td>
              <td
                class="border border-gray-600 border-opacity-50 p-4 text-gray-300"
              >
                {{ item.purpose }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="text-sm opacity-75 mb-4">
        <strong>Note:</strong> If you submit a third party's contact info, you
        must have their consent.
      </p>

      <h4 class="text-lg font-bold mt-4 mb-2">Session & Media Data</h4>
      <div class="overflow-x-auto mb-4">
        <table
          class="w-full border-collapse border border-gray-600 border-opacity-50"
        >
          <thead>
            <tr class="bg-gray-800 bg-opacity-50">
              <th
                class="border border-gray-600 border-opacity-50 p-4 text-left text-white font-bold"
              >
                Data Type
              </th>
              <th
                class="border border-gray-600 border-opacity-50 p-4 text-left text-white font-bold"
              >
                Examples
              </th>
              <th
                class="border border-gray-600 border-opacity-50 p-4 text-left text-white font-bold"
              >
                Purpose
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in sessionMediaData"
              :key="item.dataType"
              class="hover:bg-gray-800 hover:bg-opacity-25 transition-all"
            >
              <td
                class="border border-gray-600 border-opacity-50 p-4 text-white font-medium"
              >
                {{ item.dataType }}
              </td>
              <td
                class="border border-gray-600 border-opacity-50 p-4 text-gray-300"
              >
                {{ item.examples }}
              </td>
              <td
                class="border border-gray-600 border-opacity-50 p-4 text-gray-300"
              >
                {{ item.purpose }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <h4 class="text-lg font-bold mt-4 mb-2">Preferences & Settings</h4>
      <div class="overflow-x-auto mb-4">
        <table
          class="w-full border-collapse border border-gray-600 border-opacity-50"
        >
          <thead>
            <tr class="bg-gray-800 bg-opacity-50">
              <th
                class="border border-gray-600 border-opacity-50 p-4 text-left text-white font-bold"
              >
                Data Type
              </th>
              <th
                class="border border-gray-600 border-opacity-50 p-4 text-left text-white font-bold"
              >
                Examples
              </th>
              <th
                class="border border-gray-600 border-opacity-50 p-4 text-left text-white font-bold"
              >
                Purpose
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in preferencesSettingsData"
              :key="item.dataType"
              class="hover:bg-gray-800 hover:bg-opacity-25 transition-all"
            >
              <td
                class="border border-gray-600 border-opacity-50 p-4 text-white font-medium"
              >
                {{ item.dataType }}
              </td>
              <td
                class="border border-gray-600 border-opacity-50 p-4 text-gray-300"
              >
                {{ item.examples }}
              </td>
              <td
                class="border border-gray-600 border-opacity-50 p-4 text-gray-300"
              >
                {{ item.purpose }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <h4 class="text-lg font-bold mt-4 mb-2">Device & Technical Info</h4>
      <div class="overflow-x-auto mb-4">
        <table
          class="w-full border-collapse border border-gray-600 border-opacity-50"
        >
          <thead>
            <tr class="bg-gray-800 bg-opacity-50">
              <th
                class="border border-gray-600 border-opacity-50 p-4 text-left text-white font-bold"
              >
                Data Type
              </th>
              <th
                class="border border-gray-600 border-opacity-50 p-4 text-left text-white font-bold"
              >
                Examples
              </th>
              <th
                class="border border-gray-600 border-opacity-50 p-4 text-left text-white font-bold"
              >
                Purpose
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in deviceTechnicalData"
              :key="item.dataType"
              class="hover:bg-gray-800 hover:bg-opacity-25 transition-all"
            >
              <td
                class="border border-gray-600 border-opacity-50 p-4 text-white font-medium"
              >
                {{ item.dataType }}
              </td>
              <td
                class="border border-gray-600 border-opacity-50 p-4 text-gray-300"
              >
                {{ item.examples }}
              </td>
              <td
                class="border border-gray-600 border-opacity-50 p-4 text-gray-300"
              >
                {{ item.purpose }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <h4 class="text-lg font-bold mt-4 mb-2">Usage Metrics</h4>
      <p class="mb-4">
        If you opt in to share usage analytics, we may collect:
      </p>
      <div class="overflow-x-auto mb-4">
        <table
          class="w-full border-collapse border border-gray-600 border-opacity-50"
        >
          <thead>
            <tr class="bg-gray-800 bg-opacity-50">
              <th
                class="border border-gray-600 border-opacity-50 p-4 text-left text-white font-bold"
              >
                Examples
              </th>
              <th
                class="border border-gray-600 border-opacity-50 p-4 text-left text-white font-bold"
              >
                Purpose
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in usageMetricsData"
              :key="item.examples"
              class="hover:bg-gray-800 hover:bg-opacity-25 transition-all"
            >
              <td
                class="border border-gray-600 border-opacity-50 p-4 text-gray-300"
              >
                {{ item.examples }}
              </td>
              <td
                class="border border-gray-600 border-opacity-50 p-4 text-gray-300"
              >
                {{ item.purpose }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="text-sm opacity-75 mb-4">
        These metrics are anonymized or pseudonymized when possible.
      </p>

      <h4 class="text-lg font-bold mt-4 mb-2">Support & Feedback</h4>
      <div class="overflow-x-auto mb-4">
        <table
          class="w-full border-collapse border border-gray-600 border-opacity-50"
        >
          <thead>
            <tr class="bg-gray-800 bg-opacity-50">
              <th
                class="border border-gray-600 border-opacity-50 p-4 text-left text-white font-bold"
              >
                Data Type
              </th>
              <th
                class="border border-gray-600 border-opacity-50 p-4 text-left text-white font-bold"
              >
                Examples
              </th>
              <th
                class="border border-gray-600 border-opacity-50 p-4 text-left text-white font-bold"
              >
                Purpose
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in supportFeedbackData"
              :key="item.dataType"
              class="hover:bg-gray-800 hover:bg-opacity-25 transition-all"
            >
              <td
                class="border border-gray-600 border-opacity-50 p-4 text-white font-medium"
              >
                {{ item.dataType }}
              </td>
              <td
                class="border border-gray-600 border-opacity-50 p-4 text-gray-300"
              >
                {{ item.examples }}
              </td>
              <td
                class="border border-gray-600 border-opacity-50 p-4 text-gray-300"
              >
                {{ item.purpose }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <h4 class="text-lg font-bold mt-4 mb-2">What We Don't Collect</h4>
      <ul class="list-disc list-inside">
        <li>
          We do <strong>not</strong> collect or store passwords (we use
          passwordless/DID-based login).
        </li>
        <li>
          We do <strong>not</strong> use third-party trackers or behavioral
          advertising on the platform.
        </li>
        <li>
          We do <strong>not</strong> access your device camera, mic, or storage
          without explicit in-app permission for any purpose other than the
          communication and post-production uses of the platform.
        </li>
      </ul>

      <h3 class="text-2xl font-bold mt-4">Legal Basis for Processing</h3>
      <p>
        Depending on your interaction with Decast, our legal bases may include:
      </p>
      <ul class="list-disc list-inside">
        <li>
          <strong>Consent</strong> - for optional features, like newsletters or
          analytics.
        </li>
        <li>
          <strong>Contract</strong> - for providing core services you've opted
          into.
        </li>
        <li>
          <strong>Legitimate Interest</strong> - improving service reliability
          and security.
        </li>
        <li><strong>Legal Obligation</strong> - where required by law.</li>
      </ul>

      <h3 class="text-2xl font-bold mt-2">How We Use Your Data</h3>
      <p>
        At Decast, we use your data only for the purposes aligned with providing
        our decentralized streaming and collaboration platform. Usage is lawful,
        transparent, and guided by user consent and preferences.
      </p>

      <h4 class="text-xl font-bold mb-2">
        1. Service Operation & Access Control
      </h4>
      <p class="mb-2">
        <strong>Authentication & Account Management:</strong> We use your DID or
        wallet address, optional email, and profile data to manage your
        identity, securely sign you in, and administer your account.
      </p>
      <p class="mb-4">
        <strong>Session & Cast Functionality:</strong> Stream metadata and
        settings enable cast creation, joining, scheduling, recording controls,
        and playback.
      </p>

      <h3 class="text-xl font-bold mt-4 mb-2">
        2. Communication & Collaboration
      </h3>
      <p class="mb-2">
        <strong>Invitations & Collaboration:</strong> Contacts and referral
        information (e.g. invitee email or DID) are used to send invitations to
        join casts or collaborate directly.
      </p>
      <p class="mb-4">
        <strong>In-Cast Messaging & Context:</strong> Chat messages, polls, Q&A,
        and transcripts support real-time interactivity, feedback tools, and
        accessibility.
      </p>

      <h3 class="text-xl font-bold mt-4 mb-2">
        3. Content Storage & Media Use
      </h3>
      <p class="mb-2">
        <strong>Recordings & File Handling:</strong> Media content you record or
        upload (video/audio, documents) is stored and accessible for playback,
        sharing, editing, or export.
      </p>
      <p class="mb-4">
        <strong>Transcripts & Annotations:</strong> Use data from chat and
        transcripts to enhance accessibility, enable search, and power features
        like transcript editing or auto-tagging.
      </p>

      <h3 class="text-xl font-bold mt-4 mb-2">
        4. Analytics & Service Improvement (Opt‑In)
      </h3>
      <p class="mb-2">
        <strong>Usage Metrics:</strong> If you opt in, we collect
        anonymized/session‑based logs like feature usage (e.g. screen share,
        emoji use), session durations, and navigation patterns to understand how
        people use Decast and to improve performance and design.
      </p>
      <p class="mb-4">
        <strong>Diagnostics & Error Reporting:</strong> Device and crash reports
        help us troubleshoot issues and enhance platform stability.
      </p>

      <h3 class="text-xl font-bold mt-4 mb-2">5. Support & Notifications</h3>
      <p class="mb-2">
        <strong>Customer Support:</strong> Information from support requests or
        feedback you provide is used to address your issues and improve our
        support processes.
      </p>
      <p class="mb-4">
        <strong>Platform Notifications:</strong> Optional email or in-app alerts
        are used to inform you about event invitations, cast updates, beta
        features, or policy changes.
      </p>

      <h3 class="text-xl font-bold mt-4 mb-2">
        6. Security & Abuse Prevention
      </h3>
      <p class="mb-2">
        <strong>Fraud & Abuse Detection:</strong> Device metadata (IP
        address/location, OS, browser type) and logs help us detect misuse,
        spam, or suspicious behavior and act swiftly.
      </p>
      <p class="mb-4">
        <strong>System Safety:</strong> We use aggregated usage and diagnostic
        data to protect Decast from security threats and ensure consistent
        uptime.
      </p>

      <h3 class="text-xl font-bold mt-4 mb-2">
        7. Legal & Compliance Obligations
      </h3>
      <p class="mb-2">
        <strong>Compliance:</strong> We may process or disclose data as required
        by law or in response to valid legal requests (e.g. court orders).
      </p>
      <p class="mb-4">
        <strong>Consent Management:</strong> If you nominate contacts by
        providing third-party contact details, you confirm that you have their
        permission. Such individuals can opt-out as provided in our policy.
      </p>

      <h3 class="text-xl font-bold mt-4 mb-2">Additional Principles</h3>
      <ul class="list-disc list-inside mb-4">
        <li>
          <strong>Purpose Limitations:</strong> We only use your data for the
          specific purposes you consented to (e.g. opt-in analytics) or for
          service delivery and legal compliance.
        </li>
        <li>
          <strong>No Advertising or Profiling:</strong> We do not use your data
          for behavioral advertising, sell data to third parties, or create
          profile-based marketing campaigns.
        </li>
        <li>
          <strong>User-Controlled Consent:</strong> You may enable or disable
          analytics at any time, withdraw consent, or opt out of certain
          features without losing essential service access.
        </li>
      </ul>

      <h3 class="text-xl font-bold mt-4 mb-2">
        Summary Table: How We Use Your Data
      </h3>
      <div class="overflow-x-auto mb-4">
        <table
          class="w-full border-collapse border border-gray-600 border-opacity-50"
        >
          <thead>
            <tr class="bg-gray-800 bg-opacity-50">
              <th
                class="border border-gray-600 border-opacity-50 p-4 text-left text-white font-bold"
              >
                Data Category
              </th>
              <th
                class="border border-gray-600 border-opacity-50 p-4 text-left text-white font-bold"
              >
                Use Case / Purpose
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in dataUsageSummary"
              :key="item.category"
              class="hover:bg-gray-800 hover:bg-opacity-25 transition-all"
            >
              <td
                class="border border-gray-600 border-opacity-50 p-4 text-white font-medium"
              >
                {{ item.category }}
              </td>
              <td
                class="border border-gray-600 border-opacity-50 p-4 text-gray-300"
              >
                {{ item.purpose }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="mb-4 font-medium">
        We never sell your data, and we don't use invasive tracking.
      </p>

      <h2 class="text-2xl font-bold mt-4 mb-3">
        Decentralized and Encrypted Storage
      </h2>
      <ul class="list-disc list-inside mb-4">
        <li>
          Files and recordings are stored in decentralized storage systems
          (e.g., IPFS, Swarm) when possible.
        </li>
        <li>You can delete or export your data anytime.</li>
      </ul>

      <h2 class="text-2xl font-bold mt-4 mb-3">Sharing and Disclosure</h2>
      <p class="mb-2">
        We do not share your personal data with third parties except:
      </p>
      <ul class="list-disc list-inside mb-2">
        <li>
          With your explicit consent (e.g., you invite a guest to a private
          cast)
        </li>
        <li>
          With service providers who help run our infrastructure (under strict
          DPA agreements)
        </li>
        <li>When required by law (e.g., court orders or legal requests)</li>
        <li>In emergencies to prevent harm</li>
      </ul>
      <p class="mb-4">
        We minimize third-party involvement and prefer open-source, self-hosted
        solutions.
      </p>

      <h2 class="text-2xl font-bold mt-4 mb-3">Your Rights</h2>
      <p class="mb-2">You have the right to:</p>
      <ul class="list-disc list-inside mb-2">
        <li>Access your data</li>
        <li>Correct any inaccurate info</li>
        <li>Delete your data (right to be forgotten)</li>
        <li>Withdraw consent at any time</li>
        <li>Download/port your data</li>
        <li>Object to data processing where applicable</li>
      </ul>
      <p class="mb-4">
        📩 To exercise these rights, contact: privacy@decast.live
      </p>

      <h2 class="text-2xl font-bold mt-4 mb-3">Data Retention</h2>
      <p class="mb-2">
        We only retain personal data as long as necessary for the purpose
        collected. Default retention policies include:
      </p>
      <ul class="list-disc list-inside mb-2">
        <li>
          <strong>User-generated content:</strong> Stored until deleted or
          expired (e.g., free plans keep recordings for 30 days)
        </li>
        <li>
          <strong>Logs and diagnostics:</strong> Up to 90 days for system
          improvement
        </li>
        <li>
          <strong>Account info:</strong> Retained until account is deleted
        </li>
      </ul>
      <p class="mb-4">
        You can choose to set expiry dates, delete, or extend storage for any
        stored content.
      </p>

      <h2 class="text-2xl font-bold mt-4 mb-3">Children's Privacy</h2>
      <p class="mb-4">
        Decast is not intended for children under 13 (or under 16 in some
        regions). We do not knowingly collect data from minors.
      </p>

      <h2 class="text-2xl font-bold mt-4 mb-3">Cookies & Tracking</h2>
      <p class="mb-2">Decast uses minimal, privacy-friendly cookies:</p>
      <ul class="list-disc list-inside mb-2">
        <li>No third-party trackers</li>
        <li>No behavioral advertising</li>
        <li>Optional session cookies only for login and performance</li>
      </ul>
      <p class="mb-4">
        You may control or disable cookies via browser settings.
      </p>

      <h2 class="text-2xl font-bold mt-4 mb-3">Policy Updates</h2>
      <p class="mb-2">We'll notify you of major policy changes via:</p>
      <ul class="list-disc list-inside mb-2">
        <li>In-app notifications</li>
        <li>Email (if available)</li>
        <li>Updated "last revised" date on this page</li>
      </ul>
      <p class="mb-4">We encourage you to review this policy periodically.</p>

      <h2 class="text-2xl font-bold mt-4 mb-3">Contact Us</h2>
      <p class="mb-2">Questions, concerns, or complaints? Contact:</p>
      <p class="mb-4">📧 Email: privacy@decast.live</p>

      <h2 class="text-2xl font-bold mt-4 mb-3">
        Appendix: FAIR Data Society Compliance
      </h2>
      <div class="overflow-x-auto mb-4">
        <table
          class="w-full border-collapse border border-gray-600 border-opacity-50"
        >
          <thead>
            <tr class="bg-gray-800 bg-opacity-50">
              <th
                class="border border-gray-600 border-opacity-50 p-4 text-left text-white font-bold"
              >
                Principle
              </th>
              <th
                class="border border-gray-600 border-opacity-50 p-4 text-left text-white font-bold"
              >
                How Decast Fulfills It
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in fairDataCompliance"
              :key="item.principle"
              class="hover:bg-gray-800 hover:bg-opacity-25 transition-all"
            >
              <td
                class="border border-gray-600 border-opacity-50 p-4 text-white font-medium"
              >
                {{ item.principle }}
              </td>
              <td
                class="border border-gray-600 border-opacity-50 p-4 text-gray-300"
              >
                {{ item.fulfillment }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <FooterNew />
  </div>
</template>

<script>
import NavbarNew from './NavbarNew.vue';
import FooterNew from './FooterNew.vue';
export default {
  name: 'Privacy',
  components: {
    NavbarNew,
    FooterNew,
  },
  data() {
    return {
      fairDataPrinciples: [
        {
          number: '01',
          title: 'Ownership',
          description:
            'All the data related to an individual are part of their digital self and in their control with right to access, rights to self-govern, and rights to deletion.',
        },
        {
          number: '02',
          title: 'Zero-data',
          description:
            'Privacy and non-collection of personal data are the default settings. Any change in default is user consented, made aware, and revocable.',
        },
        {
          number: '03',
          title: 'Control',
          description:
            'The rights to data created about or by an individual are theirs and possibly shared with the participants as co-creators. Total or Fractional Ownership and Access of data must be possible.',
        },
        {
          number: '04',
          title: 'Consent',
          description:
            "Individual's data can only be accessed on the basis of their consent. Consent is not perpetual, it is revocable.",
        },
        {
          number: '05',
          title: 'Value',
          description:
            "Data is a product of an individual's knowledge and has economic value. An economic ecosystem must be created with fair distribution of value between creators and consumers.",
        },
        {
          number: '06',
          title: 'Responsibility',
          description:
            'Personal data should be accessed on a need-to-know basis, anonymized before shared or monetised, and with consent of the user. Trust-less systems must manage responsibility as a commodity with distinct ownership and accountability.',
        },
        {
          number: '07',
          title: 'Influence',
          description:
            "Organisations allow the freedom of digital associations within the organisation's ecosystem and recognise associations outside.",
        },
        {
          number: '08',
          title: 'Accountability',
          description:
            'Organizations that access and process personal data provide clear, understandable, and explainable information.',
        },
        {
          number: '09',
          title: 'Ethics',
          description:
            'Organisations design their IT systems based on ethical handling of all personal data; ethics cannot be an afterthought.',
        },
        {
          number: '10',
          title: 'Interoperability',
          description:
            'Open source should be the ideal in as many cases as possible when new software is released.',
        },
      ],
      accountIdentityData: [
        {
          dataType: 'DID or Wallet Address',
          examples:
            'Public key or Decentralized Identifier (e.g., did:decast:xyz)',
          purpose: 'Used for secure, decentralized login and identity',
        },
        {
          dataType: 'Optional Profile Info',
          examples: 'Name, avatar, bio, social handles (user-provided)',
          purpose: 'Display in profile, cast introductions',
        },
        {
          dataType: 'Email (Optional)',
          examples: 'For invited guests, contact-based features, notifications',
          purpose: 'Invite-based access, passwordless login, notices',
        },
      ],
      contactsInvitationsData: [
        {
          dataType: 'Contact List',
          examples: 'Manually added or permission-based synced contacts',
          purpose: 'Facilitate invitations and collaboration',
        },
        {
          dataType: 'Referrals & Invites',
          examples: 'Email or DID of invitee, message body',
          purpose: 'Send guest invitations, track RSVP status',
        },
      ],
      sessionMediaData: [
        {
          dataType: 'Stream Metadata',
          examples: 'Cast name, description, participants, start/end time',
          purpose: 'Show casts, analytics, and performance',
        },
        {
          dataType: 'Recordings & Files',
          examples: 'User-uploaded or recorded media (video, audio, docs)',
          purpose: 'Provide playback, storage, and sharing',
        },
        {
          dataType: 'Transcripts & Chat',
          examples: 'In-cast messaging, polls, questions, transcripts',
          purpose: 'Enhance accessibility and interactivity',
        },
      ],
      preferencesSettingsData: [
        {
          dataType: 'Audio/Video Settings',
          examples: 'Preferred mic/camera, mute status, resolution',
          purpose: 'Improve user experience across devices',
        },
        {
          dataType: 'Recording Controls',
          examples: 'Consent toggle, auto-record options',
          purpose: 'Respect user choice, enforce policy',
        },
        {
          dataType: 'UI Preferences',
          examples: 'Language, dark/light mode',
          purpose: 'Customize interface based on user choices',
        },
      ],
      deviceTechnicalData: [
        {
          dataType: 'Device Info',
          examples: 'OS version, browser type, device type',
          purpose: 'UI compatibility, bug resolution',
        },
        {
          dataType: 'IP Address',
          examples: 'Country/city-level geolocation (not stored long term)',
          purpose: 'Regional features, DDoS protection',
        },
        {
          dataType: 'Diagnostic Logs',
          examples: 'Errors, crash reports, performance data',
          purpose: 'Debugging and service improvement',
        },
      ],
      usageMetricsData: [
        {
          examples: 'Feature usage (e.g., screen sharing, emojis)',
          purpose: 'Understand user needs, improve experience',
        },
        {
          examples: 'Interaction timestamps (join/leave times)',
          purpose: 'Monitor session flow, optimize performance',
        },
        {
          examples: 'Navigation within Decast',
          purpose: 'Enhance UI design and navigation',
        },
      ],
      supportFeedbackData: [
        {
          dataType: 'User support messages',
          examples: 'Messages you send to our support team',
          purpose: 'Respond to issues, track resolution',
        },
        {
          dataType: 'Feedback forms',
          examples: 'Polls, surveys, or ratings after a cast',
          purpose: 'Improve features, satisfaction monitoring',
        },
      ],
      dataUsageSummary: [
        {
          category: 'Account, DID, Email',
          purpose: 'Authentication, account setup, identity verification',
        },
        {
          category: 'Stream metadata & settings',
          purpose: 'Cast creation, scheduling, and real-time session control',
        },
        {
          category: 'Recordings, chat, files',
          purpose: 'Storage, playback, user-access, collaboration',
        },
        {
          category: 'Transcripts, polls',
          purpose: 'Accessibility, interactivity, feedback enhancement',
        },
        {
          category: 'Usage analytics (opt‑in)',
          purpose: 'Feature review, platform performance, UI improvement',
        },
        {
          category: 'Diagnostics and logs',
          purpose: 'Bug fixing, troubleshooting, service reliability',
        },
        {
          category: 'Support & feedback',
          purpose: 'Customer service response, product enhancement',
        },
        {
          category: 'Device & technical info',
          purpose: 'Security monitoring, abuse prevention',
        },
        {
          category: 'Contact referrals',
          purpose: 'Invitation delivery, RSVP tracking',
        },
        {
          category: 'Legal disclosures',
          purpose: 'Regulatory compliance, lawful process enforcement',
        },
      ],
      fairDataCompliance: [
        {
          principle: 'Respect for Human Rights',
          fulfillment: 'Users own their data and control access',
        },
        {
          principle: 'Transparency',
          fulfillment: 'Clear communication and accessible policies',
        },
        {
          principle: 'Interoperability',
          fulfillment:
            'Use of open standards (DIDs, Web3 Wallet, decentralized storage)',
        },
        {
          principle: 'Reversibility',
          fulfillment: 'Users can delete, export, and revoke access anytime',
        },
        {
          principle: 'Privacy by Design',
          fulfillment: 'Minimal, encrypted, and user-consented processing',
        },
      ],
    };
  },
};
</script>

<style scoped>
.basic_privacy_cont_ {
  background-image: linear-gradient(180deg, #000, #111, #222);
  color: #a6a6a6;
}

.basic_child_privacy_cont_ {
  color: #a6a6a6;
}

h1,
h2,
h3,
h4 {
  color: #a6a6a6;
}

.fair-data-principles {
  font-family: 'JetBrains Mono', monospace;
}

.principle-card {
  background-color: rgba(0, 0, 0, 0.3);
  border-radius: 4px;
}

.principle-number {
  color: #d7df23;
  min-width: 2rem;
  font-family: 'JetBrains Mono', monospace;
}

.text-primary {
  color: #d7df23;
}

.text-white {
  color: #ffffff;
}
</style>
